import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell, Field, inputCls, SubmitBtn } from "@/components/student/AuthShell";
import { PasswordInput } from "@/components/student/PasswordInput";
import { apiError } from "@/lib/student-auth";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function strengthOf(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

const LABELS = ["Too short", "Weak", "Okay", "Strong", "Excellent"];
const COLORS = ["bg-rose-500", "bg-rose-500", "bg-amber-500", "bg-emerald-500", "bg-[#06B6D4]"];

export default function StudentRegister() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const score = useMemo(() => strengthOf(form.password), [form.password]);
  const mismatch = form.confirm.length > 0 && form.confirm !== form.password;

  const submit = async (e) => {
    e.preventDefault();
    if (mismatch) return setError("Passwords do not match");
    if (form.password.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/student/register`, {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
      });
      toast.success(`Account created — your Student ID is ${data.student_id}`);
      nav("/student/login");
    } catch (err) {
      setError(apiError(err?.response?.data?.detail, "Could not create your account"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testid="student-register-page"
      eyebrow="Create account"
      title="Start learning today."
      subtitle="Register to access your courses, training material and certificates."
      footer={
        <>
          Already enrolled?{" "}
          <Link to="/student/login" data-testid="link-to-login" className="text-[#06B6D4] font-medium">
            Log in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} data-testid="student-register-form">
        <Field label="Full Name">
          <input
            data-testid="register-name"
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            placeholder="Your full name"
          />
        </Field>
        <Field label="Email ID">
          <input
            data-testid="register-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Password" hint="Minimum 8 characters">
          <PasswordInput
            testid="register-password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls}
            placeholder="••••••••"
          />
          {form.password && (
            <div data-testid="password-strength" className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden flex gap-0.5">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`flex-1 rounded-full transition-colors ${i < score ? COLORS[score] : "bg-white/5"}`}
                  />
                ))}
              </div>
              <span className="text-[11px] text-slate-400">{LABELS[score]}</span>
            </div>
          )}
        </Field>
        <Field label="Confirm Password">
          <PasswordInput
            testid="register-confirm"
            required
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className={inputCls}
            placeholder="••••••••"
          />
          {mismatch && (
            <span data-testid="confirm-mismatch" className="block mt-1.5 text-[11px] text-rose-300">
              Passwords do not match
            </span>
          )}
        </Field>
        <Field label="Mobile Number (optional)">
          <input
            data-testid="register-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={inputCls}
            placeholder="10-digit mobile"
          />
        </Field>

        {error && (
          <div data-testid="register-error" className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-200">
            {error}
          </div>
        )}

        <SubmitBtn loading={loading} testid="register-submit">
          Create my account
        </SubmitBtn>
      </form>
    </AuthShell>
  );
}
